import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE_CODE } from '@/app/i18n';
import { useEffect, useRef, useState } from 'react';
import { normalizeLanguageCode } from '@/shared/lib';
import { useApplyAuthPayload } from './useApplyAuthPayload';
import { setAuthInitialized, useAppDispatch } from '@/app/store';
import { GlobalUserAuthProvider } from '@/app/store/api/restApi';
import { useAuthOAuthStartMutation, useUpdateClientProfileMutation } from '@/entities/auth/api/authApi';
import { AUTH_BROADCAST_CHANNEL, AuthBroadcastMessageType, type AuthBroadcastMessage } from '@/entities/auth';

interface UseOAuthLoginOptions {
  onSuccess?: () => void;
}

export const useOAuthLogin = ({ onSuccess }: UseOAuthLoginOptions = {}) => {
  const { t, i18n } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const applyAuthPayload = useApplyAuthPayload();
  const [authOAuthStart, { isLoading: isStarting }] = useAuthOAuthStartMutation();
  const [updateClientProfile] = useUpdateClientProfileMutation();

  const [pending, setPending] = useState<GlobalUserAuthProvider | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const authTabRef = useRef<Window | null>(null);

  const closeAuthTab = () => {
    authTabRef.current?.close();
    authTabRef.current = null;
  };

  const failWith = (message: string) => {
    setErrorMessage(message);
    setPending(null);
  };

  const handlerRef = useRef<(event: MessageEvent<AuthBroadcastMessage>) => void>(() => undefined);

  handlerRef.current = (event) => {
    if (event.data?.type === AuthBroadcastMessageType.MagicLinkSuccess) return;

    closeAuthTab();
    window.focus();
    const data = event.data;
    if (data?.type !== AuthBroadcastMessageType.OAuthSuccess) {
      failWith(t('login.startError'));
      return;
    }
    if (!data.payload.profile) {
      failWith(t('login.profileMissing'));
      return;
    }
    const { profile, auth } = data.payload;
    applyAuthPayload({ profile, auth });

    const guestLanguage = normalizeLanguageCode(i18n.language);
    if (guestLanguage && guestLanguage !== profile.languageCode && guestLanguage !== DEFAULT_LANGUAGE_CODE) {
      updateClientProfile({ updateClientProfileDto: { languageCode: guestLanguage } });
    }

    dispatch(setAuthInitialized());
    onSuccess?.();
    setPending(null);
  };

  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => handlerRef.current(event);
    return () => {
      channel.close();
      closeAuthTab();
    };
  }, []);

  useEffect(() => {
    if (pending === null) return;
    const id = window.setInterval(() => {
      if (authTabRef.current?.closed) {
        authTabRef.current = null;
        setPending(null);
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [pending]);

  const startLogin = async (provider: GlobalUserAuthProvider) => {
    setErrorMessage(null);
    setPending(provider);
    closeAuthTab();

    // SYNCHRONOUS — must precede any await, or Safari/Firefox block the popup.
    const authTab = window.open('about:blank', '_blank');
    if (!authTab) {
      failWith(t('login.startError'));
      return;
    }
    authTabRef.current = authTab;

    try {
      const result = await authOAuthStart({
        provider,
        clientOAuthStartDto: { languageCode: normalizeLanguageCode(i18n.language) },
      }).unwrap();
      authTab.location.href = result.data.authorizationUrl;
    } catch {
      closeAuthTab();
      failWith(t('login.startError'));
    }
  };

  return { pending, errorMessage, isStarting, startLogin, setPending };
};
